import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Sentry, initSentry } from "@/lib/sentry";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/dev/sentry-test")({
  component: SentryTestPage,
});

// Simple shared-passphrase gate. This page is a diagnostics tool, not a
// secret data page — the gate keeps casual visitors out of the "throw an
// error" buttons. Change the passphrase if it leaks.
const PASSPHRASE = "coffman-cove-sentry";
const STORAGE_KEY = "ccl:sentry-test-unlocked";

function SentryTestPage() {
  const [unlocked, setUnlocked] = useState(false);
  const [entry, setEntry] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [shouldThrow, setShouldThrow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(STORAGE_KEY) === "1") setUnlocked(true);
  }, []);

  if (!unlocked) {
    return (
      <div className="mx-auto max-w-md space-y-4 px-4 py-16">
        <h1 className="text-2xl font-bold">Sentry test — protected</h1>
        <p className="text-sm text-muted-foreground">
          Enter the diagnostics passphrase to access the Sentry test actions.
        </p>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (entry === PASSPHRASE) {
              window.sessionStorage.setItem(STORAGE_KEY, "1");
              setUnlocked(true);
              setError(null);
            } else {
              setError("Incorrect passphrase.");
            }
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="passphrase">Passphrase</Label>
            <Input
              id="passphrase"
              type="password"
              autoComplete="off"
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <Button type="submit">Unlock</Button>
        </form>
      </div>
    );
  }

  if (shouldThrow) {
    throw new Error("Coffman Cove Sentry test — uncaught render error");
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-16">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Sentry test</h1>
          <p className="text-muted-foreground">
            Trigger a test error and confirm it appears in your Sentry issues list.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            window.sessionStorage.removeItem(STORAGE_KEY);
            setUnlocked(false);
            setEntry("");
          }}
        >
          Lock
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button
          onClick={() => {
            initSentry();
            const id = Sentry.captureException(
              new Error("Coffman Cove Sentry test — captureException"),
              { tags: { source: "manual-test" } },
            );
            setStatus(`Sent captureException — event id: ${id}`);
          }}
        >
          Send captured exception
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            initSentry();
            const id = Sentry.captureMessage(
              "Coffman Cove Sentry test — captureMessage",
              "info",
            );
            setStatus(`Sent captureMessage — event id: ${id}`);
          }}
        >
          Send test message
        </Button>
        <Button variant="destructive" onClick={() => setShouldThrow(true)}>
          Throw uncaught render error
        </Button>
      </div>

      {status ? (
        <pre className="rounded-md border bg-muted p-3 text-xs">{status}</pre>
      ) : null}

      <p className="text-xs text-muted-foreground">
        Events should show up in your Sentry project within ~30 seconds. If you don't see them,
        check that ad-blockers aren't blocking <code>ingest.us.sentry.io</code>.
      </p>
    </div>
  );
}
